import type {
  EvidenceClass,
  EvidencePolicy,
  ImageFormat,
  MediaPreparationProfile,
  MediaPurpose,
  VariantSpec,
} from './types.js';

export const operationalEvidencePhotoPurposeAllowlist = [
  'inspection-evidence',
  'tbm-evidence',
  'work-control-evidence',
  'feedback-evidence',
  'incident-evidence',
  'evacuation-drill-evidence',
  'facility-psm-remediation',
] as const satisfies readonly MediaPurpose[];

export function purposeAllowsOperationalEvidencePhoto(purpose: MediaPurpose): boolean {
  return (operationalEvidencePhotoPurposeAllowlist as readonly string[]).includes(purpose);
}

export function purposeAllowsEducationEvidencePhoto(purpose: MediaPurpose): boolean {
  return purpose === 'legal-obligation-evidence';
}

const commonVariants = (format: ImageFormat): readonly VariantSpec[] => [
  { kind: 'thumbnail', maxLongEdge: 360, format, quality: 0.7 },
  { kind: 'preview', maxLongEdge: 1280, format, quality: 0.78 },
  { kind: 'evidence', maxLongEdge: 2048, format, quality: 0.84 },
];

export function policyForEvidenceClass(
  evidenceClass: EvidenceClass,
  options: { webpSupported?: boolean; profile?: MediaPreparationProfile } = {},
): EvidencePolicy {
  if (options.profile === 'tbm-signature-pdf') return {
    evidenceClass, preserveOriginal: true, immutableAfterCommit: true,
    legalHoldEligible: true, variants: [],
  };
  const compatibleFormat: ImageFormat =
    options.webpSupported === false ? 'image/jpeg' : 'image/webp';
  const preserveOriginal = [
    'serious_risk',
    'incident',
    'legal_evidence',
    'approval_locked',
  ].includes(evidenceClass);

  const inspectionVariants: readonly VariantSpec[] = [
    {
      kind: 'thumbnail',
      maxLongEdge: 480,
      exactWidth: 480,
      exactHeight: 360,
      format: 'image/webp',
      quality: 0.82,
      minimumQuality: 0.58,
      maxBytes: 256 * 1024,
    },
    {
      kind: 'evidence',
      maxLongEdge: 1600,
      exactWidth: 1600,
      exactHeight: 1200,
      format: 'image/webp',
      quality: 0.82,
      minimumQuality: 0.58,
      maxBytes: 2 * 1024 * 1024,
    },
  ];
  return {
    evidenceClass,
    preserveOriginal,
    immutableAfterCommit: preserveOriginal,
    legalHoldEligible: preserveOriginal,
    variants:
      options.profile === 'operational-evidence-photo' ||
      options.profile === 'education-evidence-photo'
        ? inspectionVariants
        : commonVariants(compatibleFormat),
  };
}

export interface PreservationDecision {
  uploadOriginal: boolean;
  reason: string;
  mayDiscardLocalSourceAfterVerifiedUpload: boolean;
}

export function decideOriginalPreservation(
  evidenceClass: EvidenceClass,
  flags: { legalHold?: boolean; approvalCommitted?: boolean; explicitPreserve?: boolean } = {},
): PreservationDecision {
  if (flags.legalHold) {
    return {
      uploadOriginal: true,
      reason: 'legal_hold',
      mayDiscardLocalSourceAfterVerifiedUpload: false,
    };
  }
  if (flags.approvalCommitted || evidenceClass === 'approval_locked') {
    return {
      uploadOriginal: true,
      reason: 'approval_immutable',
      mayDiscardLocalSourceAfterVerifiedUpload: false,
    };
  }
  if (flags.explicitPreserve) {
    return {
      uploadOriginal: true,
      reason: 'explicit_preservation',
      mayDiscardLocalSourceAfterVerifiedUpload: false,
    };
  }
  const policy = policyForEvidenceClass(evidenceClass);
  return policy.preserveOriginal
    ? {
        uploadOriginal: true,
        reason: `evidence_class:${evidenceClass}`,
        mayDiscardLocalSourceAfterVerifiedUpload: false,
      }
    : {
        uploadOriginal: false,
        reason: 'optimized_derivatives_sufficient',
        mayDiscardLocalSourceAfterVerifiedUpload: true,
      };
}

/** AVIF is delivery-only until mobile Safari encode/decode coverage is proven by telemetry. */
export function selectDeliveryFormat(capabilities: { avif: boolean; webp: boolean }): ImageFormat {
  if (capabilities.avif) return 'image/avif';
  if (capabilities.webp) return 'image/webp';
  return 'image/jpeg';
}
